import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Stack } from '@mui/material';
import { useIntl } from 'react-intl';
import { useFormContext, useWatch } from 'react-hook-form';
import { ObjectSchema } from 'yup';

import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { debounce } from '../../../../utils/debounce';
import { searchCustomers as searchCustomersThunk } from '../../../../store/series/thunks';
import { resetGetSeriesById, resetSearchCustomers } from '../../../../store/series/slice';
import { CustomerSearchRow } from '../../../../store/series/types';
import { IAppointmentFormValues } from '../../types';

import { OutlinedInput } from '../../subcomponents';
import { SearchDropdown } from './SearchDropdown';
import { SelectedCustomerCard } from './SelectedCustomerCard';
import { InlineCustomerForm } from './InlineCustomerForm';

import styles from './CustomerSearchSection.module.scss';

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_CHARS = 2;

type ViewMode = 'search' | 'selected' | 'new';

type CustomerSearchSectionProps = {
  schema: ObjectSchema<any>;
  isFormDirty: boolean;
};

function CustomerSearchSectionComponent({ schema, isFormDirty }: CustomerSearchSectionProps) {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const searchCustomers = useAppSelector((state) => state.series.searchCustomers);
  const form = useFormContext<IAppointmentFormValues>();

  // View mode is fully derived from form.customerId so cross-tab sync (via
  // sharedValues → form defaults reset) updates the rendered view without
  // needing imperative state syncing on re-mount.
  //   undefined → 'search'
  //   '' (sentinel) → 'new' (inline new-customer form)
  //   <id> → 'selected'
  const watchedCustomerId = useWatch({ control: form.control, name: 'customerId' });
  const watchedSeriesId = useWatch({ control: form.control, name: 'seriesId' });

  const viewMode: ViewMode = useMemo(() => {
    if (watchedCustomerId) return 'selected';
    if (watchedCustomerId === '') return 'new';
    return 'search';
  }, [watchedCustomerId]);

  const [searchValue, setSearchValue] = useState('');
  const [activeQuery, setActiveQuery] = useState('');

  const debouncedSearch = useMemo(() => debounce((query: string) => {
    setActiveQuery(query);
    dispatch(searchCustomersThunk({ q: query }));
  }, SEARCH_DEBOUNCE_MS), [dispatch]);

  useEffect(() => () => {
    debouncedSearch.cancel();
    dispatch(resetSearchCustomers());
    dispatch(resetGetSeriesById());
  }, [debouncedSearch, dispatch]);

  const clearCustomerFields = useCallback(() => {
    form.setValue('customerId', undefined, { shouldDirty: true, shouldValidate: true });
    form.setValue('seriesId', undefined, { shouldDirty: true, shouldValidate: true });
    form.setValue('gender', null, { shouldDirty: true });
    form.setValue('firstName', '', { shouldDirty: true });
    form.setValue('lastName', '', { shouldDirty: true });
    form.setValue('email', '', { shouldDirty: true });
    form.setValue('phoneNumber', '', { shouldDirty: true });
    form.setValue('note', null, { shouldDirty: true });
    form.setValue('earmold', false, { shouldDirty: true });
  }, [form]);

  const handleSearchChange = useCallback((value: string | null) => {
    const next = value || '';
    setSearchValue(next);

    const trimmed = next.trim();
    if (trimmed.length < SEARCH_MIN_CHARS) {
      debouncedSearch.cancel();
      setActiveQuery('');
      dispatch(resetSearchCustomers());
      return;
    }

    debouncedSearch(trimmed);
  }, [debouncedSearch, dispatch]);

  const handleRowSelect = useCallback((row: CustomerSearchRow) => {
    form.setValue('customerId', row.id, { shouldDirty: true, shouldValidate: true });
    form.setValue('firstName', row.name || '', { shouldDirty: true });
    form.setValue('lastName', row.lastName || '', { shouldDirty: true });
    form.setValue('email', row.email || '', { shouldDirty: true });
    form.setValue('phoneNumber', row.phoneNumber || '', { shouldDirty: true });

    if (row.activeSeriesId) {
      form.setValue('seriesId', row.activeSeriesId, { shouldDirty: true, shouldValidate: true });
    } else {
      form.setValue('seriesId', undefined, { shouldDirty: true });
    }

    debouncedSearch.cancel();
  }, [debouncedSearch, form]);

  const handleChange = useCallback(() => {
    clearCustomerFields();
    dispatch(resetGetSeriesById());
  }, [clearCustomerFields, dispatch]);

  const handleAddCustomer = useCallback(() => {
    clearCustomerFields();
    form.setValue('customerId', '', { shouldDirty: true });
  }, [clearCustomerFields, form]);

  const handleBackToSearch = useCallback(() => {
    clearCustomerFields();
  }, [clearCustomerFields]);

  const trimmedQuery = searchValue.trim();
  const hasSettledResults = searchCustomers.data !== null && !searchCustomers.loading;
  const hasError = searchCustomers.error !== null;
  const showDropdown = trimmedQuery.length >= SEARCH_MIN_CHARS && (hasSettledResults || hasError);

  if (viewMode === 'selected' && watchedSeriesId) {
    return (
      <Stack direction="column" spacing={1}>
        <SelectedCustomerCard
          seriesId={watchedSeriesId}
          isFormDirty={isFormDirty}
          onChange={handleChange}
        />
      </Stack>
    );
  }

  if (viewMode === 'new') {
    return (
      <InlineCustomerForm
        schema={schema}
        onBackToSearch={handleBackToSearch}
      />
    );
  }

  return (
    <Stack direction="column" spacing={1}>
      <div className={styles.titleRow}>
        <span
          className={styles.assignmentPill}
          data-testid="series-assignment-required-pill"
        >
          {intl.formatMessage({ id: 'seriesbooking.booking.assignmentRequired' })}
        </span>
      </div>

      <div className={styles.searchWrapper}>
        <OutlinedInput
          dataTestId="series-customer-search-input"
          placeholder={intl.formatMessage({ id: 'seriesbooking.booking.customerSearchPlaceholder' })}
          value={searchValue}
          onChange={handleSearchChange}
          autocompleteDisabled
        />
        {searchCustomers.loading && (
          <div className={styles.inputSpinner} data-testid="series-customer-search-input-spinner">
            <CircularProgress size={16} />
          </div>
        )}
        {showDropdown && (
          <SearchDropdown
            query={activeQuery}
            error={searchCustomers.error !== null}
            results={searchCustomers.data || []}
            onSelect={handleRowSelect}
            onCreate={handleAddCustomer}
          />
        )}
      </div>

      <button
        type="button"
        className={styles.addCustomerLink}
        data-testid="series-customer-add-link"
        onClick={handleAddCustomer}
      >
        {intl.formatMessage({ id: 'seriesbooking.booking.createNewCustomer' })}
      </button>
    </Stack>
  );
}

export const CustomerSearchSection = React.memo(CustomerSearchSectionComponent);
